import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, TriangleAlert, Users } from 'lucide-react';
import { RequireRole } from '../components/RequireRole';
import { RoleBadge, StatusBadge } from '../components/RoleBadge';
import { inputClass } from '../components/fields';
import { listMembers, type MemberRow } from '../lib/members';

type Load = 'loading' | 'ready' | 'error';

/**
 * Spec 004 — the member register (SC-411/412/413). Board+ only (server-enforced; RequireRole is
 * defence-in-depth). Server-side paginated (25/page), searchable by name/email, filterable by role and
 * status. Renders as a hairline-ruled table on wide screens and a stacked card list on narrow ones.
 * Four states: loading → table → empty ("no members match") → error. WCAG 2.1 AA.
 */
function MemberRegisterInner() {
  const [load, setLoad] = useState<Load>('loading');
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');

  const fetchPage = useCallback(async () => {
    setLoad('loading');
    const r = await listMembers({ page, q: q.trim() || undefined, role: role || undefined, status: status || undefined });
    if (r.kind === 'ok') {
      setRows(r.data.items);
      setTotal(r.data.total);
      setLoad('ready');
    } else {
      setLoad('error');
    }
  }, [page, q, role, status]);

  useEffect(() => {
    const t = setTimeout(() => void fetchPage(), 200); // debounce search typing
    return () => clearTimeout(t);
  }, [fetchPage]);

  const pageCount = Math.max(1, Math.ceil(total / 25));

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-16 sm:py-20">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">Board</p>
      <h1 className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink sm:text-4xl">Member register</h1>

      <div className="mt-8 flex flex-wrap items-end gap-3">
        <div className="min-w-[14rem] flex-1">
          <label htmlFor="q" className="block font-sans text-xs font-medium uppercase tracking-wide text-muted">Search</label>
          <div className="relative mt-1">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              id="q"
              type="search"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Name or email"
              className={`${inputClass} pl-9`}
            />
          </div>
        </div>
        <div>
          <label htmlFor="role" className="block font-sans text-xs font-medium uppercase tracking-wide text-muted">Role</label>
          <select id="role" value={role} onChange={(e) => { setPage(1); setRole(e.target.value); }} className={`${inputClass} mt-1`}>
            <option value="">All roles</option>
            <option value="member">Member</option>
            <option value="board">Board</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div>
          <label htmlFor="status" className="block font-sans text-xs font-medium uppercase tracking-wide text-muted">Status</label>
          <select id="status" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className={`${inputClass} mt-1`}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      <p className="mt-4 font-sans text-xs text-muted" aria-live="polite">
        {load === 'ready' ? `${total} member${total === 1 ? '' : 's'}` : ' '}
      </p>

      {load === 'loading' && (
        <div className="mt-10 flex flex-col items-center" aria-live="polite">
          <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
          <p className="mt-4 font-serif text-lg text-ink">Loading members…</p>
        </div>
      )}

      {load === 'error' && (
        <p role="alert" className="mt-8 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          We couldn’t load the register. Please refresh and try again.
        </p>
      )}

      {load === 'ready' && rows.length === 0 && (
        <div className="mt-10 flex flex-col items-center rounded-md border border-dashed border-border bg-surface/40 px-6 py-16 text-center">
          <Users aria-hidden className="h-9 w-9 text-muted" />
          <p className="mt-4 font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">No members match</p>
          <p className="mt-2 font-serif text-base text-muted">Try a different search or clear the filters.</p>
        </div>
      )}

      {load === 'ready' && rows.length > 0 && (
        <>
          {/* Wide screens: hairline-ruled table */}
          <table className="mt-6 hidden w-full border-collapse sm:table">
            <thead>
              <tr className="border-b border-border text-left font-display text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted">
                <th scope="col" className="py-2 pr-4">Name</th>
                <th scope="col" className="py-2 pr-4">Email</th>
                <th scope="col" className="py-2 pr-4">Role</th>
                <th scope="col" className="py-2 pr-4">Status</th>
                <th scope="col" className="py-2">Joined</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className="border-b border-border/50 align-middle">
                  <td className="py-3 pr-4">
                    <Link to={`/members/${m.id}`} className="font-sans text-sm font-medium text-accent-deep underline underline-offset-4 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
                      {m.displayName}
                    </Link>
                  </td>
                  <td className="break-all py-3 pr-4 font-sans text-sm text-muted">{m.email}</td>
                  <td className="py-3 pr-4"><RoleBadge role={m.role} /></td>
                  <td className="py-3 pr-4"><StatusBadge status={m.status} /></td>
                  <td className="py-3 font-sans text-sm text-muted">{new Date(m.joinedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Narrow screens: stacked cards */}
          <ul className="mt-6 space-y-3 sm:hidden">
            {rows.map((m) => (
              <li key={m.id} className="rounded-md border border-border bg-surface p-4">
                <Link to={`/members/${m.id}`} className="font-display text-base font-semibold text-accent-deep underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
                  {m.displayName}
                </Link>
                <p className="mt-1 break-all font-sans text-sm text-muted">{m.email}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <RoleBadge role={m.role} />
                  <StatusBadge status={m.status} />
                </div>
              </li>
            ))}
          </ul>

          {pageCount > 1 && (
            <nav className="mt-8 flex items-center justify-between" aria-label="Pagination">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-md border border-border px-3 py-1.5 font-sans text-sm text-ink hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40"
              >
                Previous
              </button>
              <span className="font-sans text-sm text-muted">Page {page} of {pageCount}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                className="rounded-md border border-border px-3 py-1.5 font-sans text-sm text-ink hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40"
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

export default function MemberRegister() {
  return <RequireRole min="board">{() => <MemberRegisterInner />}</RequireRole>;
}
